import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "@/ui-components/ui/dialog";
import { Button } from "@/ui-components/ui/button";
import { Rocket, ArrowRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useChangelogNotification } from "@/hooks/use-changelog-notification";

/**
 * Dialog component that appears when a new version is detected.
 * 
 * Uses `useChangelogNotification` to determine visibility.
 */
export const NewVersionDialog = ({ audience = 'customer' }: { audience?: 'customer' | 'internal' }) => {
  const { showDialog, closeDialog, latestVersion, markAsSeen } = useChangelogNotification(audience);
  const navigate = useNavigate();

  const handleOpenChangelog = () => {
    closeDialog();
    markAsSeen();
    navigate("/changelog");
  };

  return (
    <Dialog open={showDialog} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent 
        className="sm:max-w-[425px] p-0 overflow-hidden border-0 shadow-2xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-8 text-center relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-white blur-3xl"></div>
          </div>
          
          <div className="relative z-10 flex flex-col items-center">
            <div className="h-16 w-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4 shadow-inner ring-1 ring-white/30">
                <Rocket className="h-8 w-8 text-white fill-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2 tracking-tight">
              Neuigkeiten verfügbar!
            </h2>
            <p className="text-blue-100 font-medium">
              Version {latestVersion} ist da.
            </p>
          </div>
        </div>

        <div className="p-6 bg-white space-y-4">
          <div className="flex items-start gap-4">
             <div className="bg-blue-50 p-2 rounded-lg shrink-0">
                <Sparkles className="h-5 w-5 text-blue-600" />
             </div>
             <div>
                <h3 className="font-semibold text-gray-900 text-sm">Frische Updates</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                   Wir haben die Plattform verbessert. Schau dir an, was es Neues gibt, um das Beste aus SportyPost herauszuholen.
                </p>
             </div>
          </div>

          <DialogFooter className="pt-4 flex-col sm:flex-row gap-2">
            <Button 
                variant="outline" 
                onClick={closeDialog}
                className="w-full sm:w-auto text-gray-500 hover:text-gray-900"
            >
              Später
            </Button>
            <Button 
                onClick={handleOpenChangelog}
                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white gap-2 shadow-lg shadow-blue-600/20"
            >
              Zum Changelog
              <ArrowRight className="h-4 w-4" />
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
